import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { apiget } from '../../../service/api';
import { getStageForStatus } from './pipelineConfig';
import { SectionCard, SectionHeader } from '../LeadViewStyles';

dayjs.extend(relativeTime);

const StatusHistoryTimeline = ({ leadId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const result = await apiget(`lead-pipeline/status-history/${leadId}`);
        if (result && result.status === 200) {
          setHistory(result.data.result || []);
        }
      } catch (error) {
        console.error('Failed to fetch status history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchHistory();
    }
  }, [leadId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Card style={{ borderTopLeftRadius: '0px', borderTopRightRadius: '0px' }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={4}
          >
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No status changes recorded yet. Status changes will appear here when leads are moved between pipeline stages.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ borderTopLeftRadius: '0px', borderTopRightRadius: '0px' }}>
      <CardContent>
        <SectionCard sx={{ mb: 0 }}>
          <SectionHeader>Status Change History</SectionHeader>

        <Box sx={{ position: 'relative' }}>
          {history.map((entry, index) => {
            const prevStage = getStageForStatus(entry.previousStatus);
            const newStage = getStageForStatus(entry.newStatus);
            const changedBy = entry.changedBy
              ? `${entry.changedBy.firstName || ''} ${entry.changedBy.lastName || ''}`.trim()
              : 'System';

            return (
              <Box key={entry._id} sx={{ display: 'flex', mb: 2, position: 'relative' }}>
                {/* Timeline line */}
                <Box
                  sx={{
                    width: 2,
                    bgcolor: index === history.length - 1 ? 'transparent' : 'grey.300',
                    position: 'absolute',
                    left: 15,
                    top: 32,
                    bottom: -16,
                  }}
                />

                {/* Timeline dot */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: `${newStage.color}20`,
                    border: `2px solid ${newStage.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mr: 2,
                    zIndex: 1,
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16, color: newStage.color }} />
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1.5,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  {/* Status change chips */}
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <Chip
                      label={entry.previousStatus}
                      size="small"
                      sx={{
                        bgcolor: `${prevStage.color}18`,
                        color: prevStage.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        borderColor: prevStage.color,
                      }}
                      variant="outlined"
                    />
                    <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Chip
                      label={entry.newStatus}
                      size="small"
                      sx={{
                        bgcolor: `${newStage.color}18`,
                        color: newStage.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        borderColor: newStage.color,
                      }}
                      variant="outlined"
                    />
                  </Box>

                  {/* Metadata row */}
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      {changedBy}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      &middot;
                    </Typography>
                    <Typography variant="caption" color="text.disabled" title={dayjs(entry.createdOn).format('MMM D, YYYY h:mm A')}>
                      {dayjs(entry.createdOn).fromNow()}
                    </Typography>
                  </Box>

                  {/* Reason lost */}
                  {entry.reasonLost && (
                    <Box mt={1} p={1} bgcolor="error.lighter" borderRadius={1}>
                      <Typography variant="caption" color="error.dark">
                        <strong>Reason Lost:</strong> {entry.reasonLost}
                      </Typography>
                    </Box>
                  )}

                  {/* Transition note */}
                  {entry.transitionNote && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5} fontStyle="italic">
                      {entry.transitionNote}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        </SectionCard>
      </CardContent>
    </Card>
  );
};

export default StatusHistoryTimeline;
